<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'PasswordResetModal',
})
</script>
<template>
    <a-modal 
      :open="visible" 
      title="修改密码" 
      @ok="handleOk"
      @cancel="handleCancel"
      :confirmLoading="confirmLoading"
    >
      <a-form
        ref="formRef"
        :model="formState"
        name="passwordForm"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
        autocomplete="off"
      >
        <a-divider style="padding: 0px;" />
        
        <a-form-item label="用户名" name="username">
          <a-input v-model:value="formState.username" disabled />
        </a-form-item>
  
        <a-form-item
          label="原密码"
          name="oldPassword"
          :rules="[
            { required: true, message: '请输入原密码', trigger: 'blur' },
          ]"
        >
          <a-input-password v-model:value="formState.oldPassword" :visibilityToggle="false" autocomplete="new-password" />
        </a-form-item>
  
        <a-form-item 
          label="新密码" 
          name="newPassword"
          :rules="[{ required: true, message: '请输入新密码', trigger: 'blur' }]"
        >
          <a-input-password v-model:value="formState.newPassword" autocomplete="new-password" />
        </a-form-item>
  
        <a-form-item
          label="确认密码"
          name="confirmPassword"
          :rules="[
            { required: true, message: '请确认密码', trigger: 'blur' },
            {
              validator: confirmPasswordValidator,
              trigger: 'blur'
            }
          ]"
        >
          <a-input-password v-model:value="formState.confirmPassword" autocomplete="new-password" />
        </a-form-item>
        
        <a-divider />
      </a-form>
    </a-modal>
  </template>
  
  <script lang="ts" setup name="PasswordResetModal">
  // 关键修复：导入watch函数
  import { defineProps, defineEmits, ref, watch } from 'vue';
  import type { FormInstance } from 'vue';
  import axios from 'axios';
  import { message, Modal } from 'ant-design-vue';
  import { logout } from '@/views/user/service';
  import { clearUserInfo } from '@/utils/util';
  import router from '@/router';
  import ls from '/src/utils/storage';
  import { ACCESS_TOKEN } from '/src/store/mutation-types';
  
  // 定义接收的属性
  const props = defineProps({
    visible: {
      type: Boolean,
      default: false
    },
    username: {
      type: String,
      required: true,
      default: ''
    }
  });
  
  // 定义触发的事件
  const emit = defineEmits({
    'update:visible': (value: boolean) => true,
    cancel: () => true,
    success: () => true
  });
  
  // 响应式变量
  const formRef = ref<FormInstance | null>(null);
  const confirmLoading = ref<boolean>(false);
  const formState = ref({
    username: props.username,
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // 监听username变化，同步到表单
  watch(
    () => props.username,
    (newVal) => {
      formState.value.username = newVal;
    }
  );
  
  // 密码确认校验器
  const confirmPasswordValidator = async (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error('请确认密码'));
    }
    if (value !== formState.value.newPassword) {
      return Promise.reject(new Error('两次输入的密码不一致!'));
    }
    return Promise.resolve();
  };
  
  // 处理确定按钮
  const handleOk = async () => {
    if (!formRef.value) {
      message.error('表单初始化失败');
      return;
    }
  
    try {
      // 表单验证
      await formRef.value.validate();
      confirmLoading.value = true;
      
      // 准备请求参数
      const { oldPassword, newPassword, confirmPassword } = formState.value;
      const params = {
        oldpass: oldPassword,
        pass1: newPassword,
        pass2: confirmPassword
      };
  
      // 发送密码修改请求
      const response = await axios.get("/api/user/resetpass", { 
        params,
        headers: {
          Authorization: `Bearer ${ls.get(ACCESS_TOKEN) || ''}`
        }
      });
  
      if (response.data.status === 0) {
         //a
        emit('update:visible', false);
        emit('success');
        
        // 提示重新登录
        Modal.success({
          title: '修改成功',
          content: '密码已成功修改，请重新登录',
          onOk: () => {
            logout().then(() => {
              clearUserInfo();
              router.push({ path: '/user/login' });
            });
          }
        });
      } else {
        message.error(response.data.message || "密码修改失败");
      }
    } catch (error) {
      console.error('密码修改失败:', error);
      if (error.name === 'ValidateError') {
        message.error('表单填写有误，请检查');
      } else {
        message.error('网络异常，请稍后重试');
      }
    } finally {
      confirmLoading.value = false;
    }
  };
  
  // 处理取消按钮
  const handleCancel = () => {
    // 重置表单
    formRef.value?.resetFields();
    emit('update:visible', false);
    emit('cancel');
  };
  </script>
  
  <style scoped lang="less">
  /* 可根据需要添加组件特定样式 */
  </style>
  